import React, { useState } from 'react';
import { useAudit } from '../context/AuditContext';
import { SectionCard } from './SectionCard';
import { RatingGroup } from './RatingGroup';
import { TooltipWrapper, TOOLTIP_CONTENT } from './TooltipWrapper';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Settings2, X } from 'lucide-react';
import { getToolsForCategory, getTechStackSubtitle, INTEGRATION_RATING_QUESTION } from '../data/techStackTools';

export const TechStackSection = ({ category }) => {
  const { auditData, updateTechStackTools, updateTechStackOtherTool, updateTechStackRating } = useAudit();
  
  const techStack = auditData.tech_stack[category] || { tools: [], other_tool: '', integration_rating: 0 };
  const tools = getToolsForCategory(category);
  const subtitle = getTechStackSubtitle(category);
  
  // Local state for the "Other" input field
  const [otherInput, setOtherInput] = useState('');

  const handleToolToggle = (toolValue, checked) => {
    let newTools = [...techStack.tools];
    if (checked) {
      newTools.push(toolValue);
    } else {
      newTools = newTools.filter(t => t !== toolValue);
    }
    updateTechStackTools(category, newTools);
  };

  const showOtherInput = techStack.tools.includes('other');
  
  // Parse other_tool as an array (could be string or array)
  const otherTools = Array.isArray(techStack.other_tool) 
    ? techStack.other_tool 
    : (techStack.other_tool && techStack.other_tool.trim() !== '' 
        ? techStack.other_tool.split(',').map(t => t.trim()).filter(Boolean)
        : []);

  const handleAddOtherTool = (e) => {
    if (e.key === 'Enter' && otherInput.trim()) {
      e.preventDefault();
      const newOtherTools = [...otherTools, otherInput.trim()];
      updateTechStackOtherTool(category, newOtherTools);
      setOtherInput('');
    }
  };

  const handleRemoveOtherTool = (indexToRemove) => {
    const newOtherTools = otherTools.filter((_, index) => index !== indexToRemove);
    updateTechStackOtherTool(category, newOtherTools);
  };

  return (
    <SectionCard 
      title="Tools You Use" 
      description={subtitle}
      icon={Settings2}
      className="bg-gradient-to-br from-muted/30 to-background"
    >
      <div className="space-y-6">
        {/* Tool Selection */}
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-3">
          {tools.map((tool) => {
            // Parse label to separate category name from examples in parentheses
            const match = tool.label.match(/^([^(]+)(\(.+\))?$/);
            const categoryName = match ? match[1].trim() : tool.label;
            const examples = match && match[2] ? match[2] : '';
            
            return (
              <div key={tool.value} className="flex items-center gap-1 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox
                  id={`${category}-${tool.value}`}
                  checked={techStack.tools.includes(tool.value)}
                  onCheckedChange={(checked) => handleToolToggle(tool.value, checked)}
                  data-testid={`tech-${category}-${tool.value}`}
                  className="h-5 w-5 flex-shrink-0"
                />
                <Label
                  htmlFor={`${category}-${tool.value}`}
                  className="text-sm leading-snug cursor-pointer flex-1 ml-1"
                >
                  <span className="font-semibold">{categoryName}</span>
                  {examples && <span className="font-normal italic"> {examples}</span>}
                </Label>
              </div>
            );
          })}
        </div>

        {/* Other Tool Multi-Entry Tags */}
        {showOtherInput && (
          <div className="space-y-3">
            <div>
              <Label htmlFor={`${category}-other-tool`} className="text-sm font-semibold">
                Specify your tools (press Enter after each):
              </Label>
              <Input
                id={`${category}-other-tool`}
                value={otherInput}
                onChange={(e) => setOtherInput(e.target.value)}
                onKeyDown={handleAddOtherTool}
                placeholder="Type tool name or category and press Enter..."
                className="mt-2"
                data-testid={`tech-${category}-other-input`}
              />
            </div>
            
            {/* Display entered tags */}
            {otherTools.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {otherTools.map((tool, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary" 
                    className="pl-3 pr-1.5 py-1.5 text-sm flex items-center gap-2"
                  >
                    <span>{tool}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveOtherTool(index)}
                      data-testid={`tech-${category}-remove-${index}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Integration Rating with Custom Scale */}
        {techStack.tools.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Label className="text-base font-semibold">{INTEGRATION_RATING_QUESTION}</Label>
              <TooltipWrapper content={TOOLTIP_CONTENT.integrationRating} iconSize={14} />
            </div>
            
            {/* Custom Integration Rating Scale */}
            <div className="space-y-3">
              <div className="flex justify-between items-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => updateTechStackRating(category, rating)}
                    className={`flex-1 h-12 rounded-lg border-2 transition-all ${
                      techStack.integration_rating === rating
                        ? 'bg-primary text-primary-foreground border-primary shadow-lg scale-105'
                        : 'bg-background border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                    data-testid={`tech-${category}-integration-${rating}`}
                  >
                    <span className="text-lg font-bold">{rating}</span>
                  </button>
                ))}
              </div>
              
              {/* Scale Labels */}
              <div className="flex justify-between text-xs text-muted-foreground px-1">
                <span className="font-medium">Poorly</span>
                <span className="font-medium">Seamlessly</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};
